import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { MapPinIcon, CalendarIcon } from 'lucide-react';
type Category = 'All' | 'Aviation & Infrastructure' | 'Power & Energy' | 'Oil & Gas' | 'Manufacturing' | 'Development';
const projectsData = [{
  name: 'Meteorology & Air Quality Studies',
  desc: 'Comprehensive air quality monitoring and meteorological studies for airport operations.',
  location: 'Akwa Ibom International Airport',
  category: 'Aviation & Infrastructure'
}, {
  name: 'Inner Southern Expressway EIA',
  desc: 'Environmental Impact Assessment for major urban transportation corridor.',
  location: 'Abuja',
  category: 'Aviation & Infrastructure'
}, {
  name: 'Outer Southern Expressway EIA',
  desc: 'Environmental Impact Assessment for highway expansion and development.',
  location: 'Abuja',
  category: 'Aviation & Infrastructure'
}, {
  name: 'Jabi Waterfront Resort EIA',
  desc: 'Environmental assessment for premium waterfront hospitality development.',
  location: 'Abuja',
  category: 'Aviation & Infrastructure'
}, {
  name: '20MW Solar Power Projects EIA',
  desc: 'Environmental assessment for utility-scale solar installations.',
  location: 'Borno & Katsina States',
  category: 'Power & Energy'
}, {
  name: 'Gurara Hydropower Project EIA',
  desc: 'Comprehensive environmental impact study for major hydroelectric facility.',
  location: 'Kaduna State',
  category: 'Power & Energy'
}, {
  name: 'Mini-Grid Projects ESMP',
  desc: 'Environmental and Social Management Plans for rural electrification.',
  location: 'Kaduna & Rivers States',
  category: 'Power & Energy'
}, {
  name: 'Chevron Incinerator Installations',
  desc: 'Peer review of environmental compliance for waste management facilities.',
  location: 'Various Locations',
  category: 'Oil & Gas'
}, {
  name: 'LPG Depot Project EIA',
  desc: 'Environmental assessment for major gas storage and distribution facility.',
  location: 'Ondo State',
  category: 'Oil & Gas'
}, {
  name: 'Swift Oil Tank Farm EIA',
  desc: 'Comprehensive EIA for petroleum products storage terminal.',
  location: 'Lagos',
  category: 'Oil & Gas'
}, {
  name: 'Resource Refinery Project EIA',
  desc: 'Environmental impact study for modular refinery development.',
  location: 'Akwa Ibom',
  category: 'Oil & Gas'
}, {
  name: 'Dangote Flour Mills Expansion EIA',
  desc: 'Environmental assessment for major food processing facility expansion.',
  location: 'Lagos & Kano',
  category: 'Manufacturing'
}, {
  name: 'Dangote Pasta Expansion EIA',
  desc: 'Impact assessment for manufacturing capacity increase.',
  location: 'Various Locations',
  category: 'Manufacturing'
}, {
  name: 'Engineered Marble Manufacturing Plant EIA',
  desc: 'Environmental study for industrial materials production facility.',
  location: 'Nigeria',
  category: 'Manufacturing'
}, {
  name: 'Staple Crops Processing Zones ESIA',
  desc: 'Environmental and Social Impact Assessment for agricultural hubs.',
  location: 'African Development Bank Project',
  category: 'Development'
}, {
  name: 'Fadama Projects Environmental Audit',
  desc: 'Comprehensive environmental audit of agricultural development initiatives.',
  location: 'World Bank Project',
  category: 'Development'
}, {
  name: 'National Telecom Infrastructure',
  desc: 'Environmental audits and EIA support for nationwide telecom rollout.',
  location: 'Nationwide',
  category: 'Development'
}];
const categories: Category[] = ['All', 'Aviation & Infrastructure', 'Power & Energy', 'Oil & Gas', 'Manufacturing', 'Development'];
export function ProjectsPage() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const filteredProjects = activeCategory === 'All' ? projectsData : projectsData.filter(p => p.category === activeCategory);
  return <>
      <PageHeader title="Selected Projects" subtitle="A showcase of our environmental consulting work across Nigeria and beyond." data-id="element-628" />

      <section className="py-20 bg-gesl-offWhite" data-id="element-629">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-630">
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-3 mb-12" data-id="element-631">
            {categories.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-5 py-2.5 rounded-sm text-sm font-medium transition-all duration-300 ${activeCategory === category ? 'bg-gesl-dark text-white' : 'bg-white text-gesl-charcoal hover:bg-gesl-dark hover:text-white border border-gray-200'}`} data-id="element-632">
                {category}
              </button>)}
          </div>

          {/* Projects Grid */}
          <AnimatePresence mode="wait" data-id="element-633">
            <motion.div key={activeCategory} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.3
          }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-634">
              {filteredProjects.map((project, idx) => <motion.div key={project.name} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: idx * 0.05
            }} className="bg-white p-6 rounded-sm shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100" data-id="element-635">
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-gesl-warmWhite text-gesl-green rounded-full mb-4" data-id="element-636">
                    {project.category}
                  </span>
                  <h3 className="text-lg font-bold text-gesl-dark mb-3" data-id="element-637">
                    {project.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-638">
                    {project.desc}
                  </p>
                  <div className="flex items-center text-gesl-gold text-sm font-medium" data-id="element-639">
                    <MapPinIcon className="h-4 w-4 mr-2" data-id="element-640" />
                    {project.location}
                  </div>
                </motion.div>)}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
    </>;
}